import { queryOptions, useQuery } from '@tanstack/vue-query';
import type { Client } from 'openapi-fetch';
import { unwrapResponse, unwrapResponseOrThrow } from '~/utils/unwrapResponse';
import type { PublisherDataQuestionnaire } from '~~/models/PublisherDataQuestionnaire';
import type { paths } from '~~/types/Api';

export type UsePublisherDataQuestionnaireQueryOptions = {
	merchantId: MaybeRef<string | undefined>;
	enabled?: MaybeRef<boolean>;
};

export type CreatePublisherDataQuestionnaireQueryOptions = {
	apiClient: Client<paths>;
} & UsePublisherDataQuestionnaireQueryOptions;

export const createPublisherDataQuestionnaireQuery = (options: CreatePublisherDataQuestionnaireQueryOptions) => {
	const enabled = computed(() => {
		const merchantId = unref(options.merchantId);

		if (!merchantId) {
			return false;
		}

		return unref(options.enabled) ?? true;
	});

	return queryOptions({
		queryKey: ['publisherdata-questionnaire', options.merchantId],
		queryFn: async () => {
			const productCategoriesRequest = options.apiClient
				.GET('/merchants/{merchantId}/productcategories', {
					params: {
						path: { merchantId: unref(options.merchantId)! },
					},
				})
				.then(unwrapResponse);

			const publisherDataQuestionnaireRequest = options.apiClient
				.GET('/publisherdata/questionnaire', {
					params: {
						query: {
							merchantId: unref(options.merchantId)!,
						},
					},
				})
				.then(unwrapResponseOrThrow);

			const [
				productCategoriesResponse,
				publisherDataQuestionnaireResponse,
			] = await Promise.all([
				productCategoriesRequest,
				publisherDataQuestionnaireRequest,
			]);

			const requiresProductCategories = productCategoriesResponse ? Object.entries(productCategoriesResponse).length > 0 : false;

			return { ...publisherDataQuestionnaireResponse, requiresProductCategories } satisfies PublisherDataQuestionnaire;
		},
		enabled,
	});
};

export const usePublisherDataQuestionnaireQuery = (options: UsePublisherDataQuestionnaireQueryOptions) => {
	const { $api } = useNuxtApp();

	const query = useQuery(createPublisherDataQuestionnaireQuery({ apiClient: $api, ...options }));

	return query;
};
